import { useEffect, useMemo, useState } from "react";
import { api, fmtDate } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { CaretLeft, CaretRight, FilmSlate, Package } from "@phosphor-icons/react";
import { toast } from "sonner";

function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function addMonths(d, n) { return new Date(d.getFullYear(), d.getMonth() + n, 1); }
function daysInMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate(); }

export default function CalendarPage() {
  const [cursor, setCursor] = useState(startOfMonth(new Date()));
  const [events, setEvents] = useState([]);
  const [view, setView] = useState("month");
  const [dragEvent, setDragEvent] = useState(null);

  const load = () => api.get("/calendar").then(r => setEvents(r.data));
  useEffect(() => { load(); }, []);

  const label = cursor.toLocaleDateString("en-IN", { month: "long", year: "numeric" });

  const cells = useMemo(() => {
    const first = startOfMonth(cursor);
    const start = new Date(first);
    start.setDate(1 - first.getDay());
    const arr = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(start); d.setDate(start.getDate() + i);
      arr.push(d);
    }
    return arr;
  }, [cursor]);

  const evByDay = useMemo(() => {
    const map = {};
    for (const e of events) {
      const key = e.date.slice(0, 10);
      (map[key] = map[key] || []).push(e);
    }
    return map;
  }, [events]);

  const onDrop = async (date) => {
    if (!dragEvent) return;
    const iso = date.toISOString().slice(0, 10);
    try {
      await api.put("/calendar/reschedule", { event_id: dragEvent.id, new_date: iso });
      toast.success("Rescheduled");
      setDragEvent(null); load();
    } catch { toast.error("Failed"); }
  };

  const today = new Date().toISOString().slice(0, 10);
  const weekDays = useMemo(() => {
    const start = new Date(cursor); start.setDate(start.getDate() - start.getDay());
    return Array.from({ length: 7 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
  }, [cursor]);

  return (
    <>
      <PageHeader title="Calendar" subtitle="Shoots & deliveries · Drag to reschedule"
        actions={
          <div className="flex items-center gap-1">
            {["day", "week", "month"].map(v => (
              <button key={v} data-testid={`view-${v}`} onClick={() => setView(v)} className={`text-xs px-3 py-1.5 rounded-md capitalize ${view === v ? "bg-white/10 text-white" : "text-white/50 hover:text-white"}`}>{v}</button>
            ))}
          </div>
        }
      />
      <div className="p-8">
        <div className="flex items-center gap-2 mb-4">
          <button data-testid="cal-prev" onClick={() => setCursor(addMonths(cursor, -1))} className="hairline rounded-md p-2 hover:bg-white/[0.03]"><CaretLeft size={14} /></button>
          <button data-testid="cal-next" onClick={() => setCursor(addMonths(cursor, 1))} className="hairline rounded-md p-2 hover:bg-white/[0.03]"><CaretRight size={14} /></button>
          <div className="text-lg font-semibold tracking-tight ml-2">{label}</div>
          <button onClick={() => setCursor(startOfMonth(new Date()))} className="ml-auto text-xs text-white/60 hover:text-white hairline rounded-md px-3 py-1.5">Today</button>
        </div>

        {view === "month" && (
          <div className="hairline rounded-lg overflow-hidden bg-[#0F0F0F]">
            <div className="grid grid-cols-7 hairline-b">
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(d => (
                <div key={d} className="text-[10px] uppercase tracking-widest text-white/40 px-3 py-2 text-center">{d}</div>
              ))}
            </div>
            <div className="grid grid-cols-7 grid-rows-6">
              {cells.map((d, i) => {
                const iso = d.toISOString().slice(0, 10);
                const inMonth = d.getMonth() === cursor.getMonth();
                const isToday = iso === today;
                const dayEvents = evByDay[iso] || [];
                return (
                  <div key={i} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(d)}
                    className={`min-h-[100px] p-2 border-r border-b border-white/5 ${inMonth ? "" : "bg-black/40 text-white/30"} hover:bg-white/[0.02] transition-colors`}>
                    <div className={`mono text-[11px] mb-1 ${isToday ? "text-[#D97706] font-bold" : "text-white/50"}`}>{d.getDate()}</div>
                    <div className="space-y-1">
                      {dayEvents.slice(0, 3).map(e => (
                        <div key={e.id} draggable onDragStart={() => setDragEvent(e)}
                          className={`text-[10px] px-1.5 py-0.5 rounded truncate cursor-grab active:cursor-grabbing ${e.type === "shoot" ? "bg-[#D97706]/15 text-[#D97706] border border-[#D97706]/20" : "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20"}`}>
                          {e.type === "shoot" ? "▶" : "◆"} {e.title}
                        </div>
                      ))}
                      {dayEvents.length > 3 && <div className="text-[10px] text-white/40">+{dayEvents.length - 3} more</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {view === "week" && (
          <div className="hairline rounded-lg overflow-hidden bg-[#0F0F0F]">
            <div className="grid grid-cols-7">
              {weekDays.map((d, i) => {
                const iso = d.toISOString().slice(0, 10);
                const dayEvents = evByDay[iso] || [];
                return (
                  <div key={i} className="min-h-[280px] p-3 border-r border-white/5">
                    <div className="text-[10px] uppercase tracking-widest text-white/40">{d.toLocaleDateString("en-IN", { weekday: "short" })}</div>
                    <div className={`mono text-xl font-bold ${iso === today ? "text-[#D97706]" : ""}`}>{d.getDate()}</div>
                    <div className="mt-3 space-y-1.5">
                      {dayEvents.map(e => (
                        <div key={e.id} className={`text-xs px-2 py-1 rounded ${e.type === "shoot" ? "bg-[#D97706]/15 text-[#D97706]" : "bg-emerald-500/10 text-emerald-400"}`}>{e.title}</div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {view === "day" && (
          <div className="hairline rounded-lg bg-[#0F0F0F] p-6">
            <div className="text-2xl font-bold mb-4">{cursor.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" })}</div>
            <div className="space-y-2">
              {(evByDay[cursor.toISOString().slice(0, 10)] || []).map(e => (
                <div key={e.id} className="hairline rounded-md p-3 bg-white/[0.02] flex items-center gap-3">
                  {e.type === "shoot" ? <FilmSlate size={18} className="text-[#D97706]" weight="duotone" /> : <Package size={18} className="text-emerald-400" weight="duotone" />}
                  <div>
                    <div className="font-medium">{e.title}</div>
                    <div className="text-xs text-white/50">{e.location} · {e.call_time}</div>
                  </div>
                </div>
              ))}
              {(evByDay[cursor.toISOString().slice(0, 10)] || []).length === 0 && <div className="text-white/40">Nothing scheduled.</div>}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
